<template>
  <div v-if="visible" class="outing-session-banner no-print" role="status">
    <button type="button" class="outing-session-banner-btn" @click="goToTopo">
      <!-- The pulse is the promise that fixes are arriving; it must not
           outlive them. A recording gone silent, or one the browser has
           refused outright, gets the still amber dot instead. -->
      <span
        class="outing-session-banner-dot"
        :class="{
          'is-recording': $outingSession.gpsTracking && !isStalled,
          'is-paused': isPausedLike || isStalled,
        }"
      ></span>
      <span class="outing-session-banner-text">
        <strong>{{ statusLabel }}</strong>
        <small>{{ elapsedLabel }}{{ metricsLabel }}</small>
      </span>
      <!-- Names its destination. On its own the chevron only said "this
           is tappable", which left the whole banner unexplained: Gilles
           asked what it was for and why it had an arrow (mail
           2026-09-04). "Voir le topo" answers both at once. -->
      <span class="outing-session-banner-go">
        {{ $gettext('Voir la trace') }}
        <fa-icon icon="chevron-right" />
      </span>
    </button>
    <!-- The banner is the surface a paused outing is most likely to be
         seen from — the user has wandered off the topo page — so the way
         back belongs here and not only in the topo header menu. -->
    <button v-if="isPausedLike" type="button" class="outing-session-banner-resume" @click="resumeOuting">
      <fa-icon icon="play" />
      &nbsp;{{ $gettext('Reprendre') }}
    </button>
  </div>
</template>

<script>
// Global reminder that an outing session is running. Only visible when
// the user is NOT already on the topo the session is anchored to —
// otherwise the StartOutingControl in the header already shows the
// state and this banner would duplicate it.
//
// Sits inside the App shell above the BottomNav. Tapping it navigates
// back to the topo so the user can stop / adjust / add photos.

import { formatElapsed } from '@/pwa/elapsed-label';
import { distance } from '@/pwa/units';

export default {
  name: 'OutingSessionBanner',

  data() {
    return {
      now: Date.now(),
      tickHandle: null,
    };
  },

  computed: {
    topoRef() {
      return this.$outingSession?.topoRef || null;
    },
    active() {
      return !!(this.$outingSession?.sessionActive && this.topoRef);
    },
    // True when the user is currently viewing the topo where the
    // session was started — in that case the in-header control shows
    // the state, so this banner would be redundant. Uses a strict
    // match on route name + id so an unrelated outing page still
    // displays the banner (previous implementation used to swallow
    // every outing page, hiding the banner too aggressively).
    onOwnTopo() {
      if (!this.active) return false;
      const t = this.topoRef;
      const route = this.$route;
      return route.name === t.type && String(route.params.id) === String(t.id);
    },
    visible() {
      return this.active && !this.onOwnTopo;
    },
    isPausedLike() {
      return this.$outingSession.paused || this.$outingSession.recordingInterrupted;
    },

    // Recording is on, but nothing is coming in — or the browser has
    // refused to try. Both look identical from the outside (a trace that
    // stops growing), and neither used to be said anywhere.
    isStalled() {
      return !!this.$outingSession.geoError || !!this.$outingSession.storageError || this.$outingSession.gpsSilent;
    },

    // The states, most alarming first: a trace that is no longer being
    // saved, an outright refusal, a recording gone quiet, then the two
    // the app already knew.
    statusLabel() {
      if (this.$outingSession.storageError) return this.$gettext('Trace non sauvegardée');
      if (this.$outingSession.geoError) return this.$gettext('GPS bloqué');
      if (this.$outingSession.gpsSilent) return this.$gettext('Aucun point reçu');
      if (this.$outingSession.recordingInterrupted) return this.$gettext('Enregistrement interrompu');
      if (this.$outingSession.autoResumed) return this.$gettext('Enregistrement repris');
      if (this.$outingSession.paused) return this.$gettext('Sortie en pause');
      return this.$gettext('Sortie en cours');
    },

    elapsedLabel() {
      return formatElapsed(
        this.$outingSession.startedAt,
        this.now,
        this.$outingSession.pausedMs,
        this.$outingSession.pausedAt
      );
    },
    metricsLabel() {
      const metres = this.$outingSession.tracedDistanceMeters;
      if (metres < 50) return '';
      const shown = distance(metres, this.$appSettings?.units);
      return ` · ${shown.value} ${shown.unit}`;
    },
  },

  watch: {
    // Two effects, one condition. The tick: only run the elapsed-time
    // interval while there is actually a session to display — idle
    // branches (no session, or user on the session's own topo) don't
    // schedule it at all. The attribute: reserve room at the bottom of
    // the scroll surface while the banner is up, since it floats over
    // .page-content and was covering the last field of the page
    // underneath (visible on Gilles' screenshots, mail 2026-09-04).
    visible: {
      handler(now) {
        if (now) this.startTick();
        else this.stopTick();
        this.reserveRoom(now);
      },
      immediate: true,
    },
  },

  beforeDestroy() {
    this.stopTick();
    this.reserveRoom(false);
  },

  methods: {
    // A data attribute rather than a class, so it reads plainly in the
    // inspector next to data-shell and the CSS rule can be scoped to the
    // mobile shell — the only place where .page-content is the scrolling
    // element.
    reserveRoom(active) {
      if (typeof document === 'undefined') return;
      if (active) document.documentElement.setAttribute('data-session-banner', 'true');
      else document.documentElement.removeAttribute('data-session-banner');
    },

    resumeOuting() {
      this.$outingSession.resume();
    },

    startTick() {
      if (this.tickHandle) return;
      this.now = Date.now();
      this.tickHandle = window.setInterval(() => {
        this.now = Date.now();
      }, 30000);
    },
    stopTick() {
      if (this.tickHandle) {
        window.clearInterval(this.tickHandle);
        this.tickHandle = null;
      }
    },
    goToTopo() {
      // The live map, not the topo: someone tapping a "sortie en cours"
      // badge is asking what the recording is doing, and the topo page
      // answers a different question. It is one tap from there.
      this.$router.push({ name: 'outing-live' }).catch(() => {
        /* NavigationDuplicated is benign */
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.outing-session-banner {
  position: fixed;
  left: 0;
  right: 0;
  // Just above the BottomNav (66 + safe-area). The margin keeps it
  // pinned inside the safe area on iPhone home-indicator devices.
  bottom: calc(66px + env(safe-area-inset-bottom) + 8px);
  z-index: 27;
  display: flex;
  justify-content: center;
  padding: 0 0.75rem;
  pointer-events: none;
}

.outing-session-banner-btn {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.5rem 0.85rem 0.5rem 0.7rem;
  background: #4a4a4a;
  color: white;
  border: none;
  border-radius: 999px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  font-size: 0.8rem;
  line-height: 1.2;
  max-width: 100%;
}

.outing-session-banner-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #a8a8a8;
  flex: 0 0 auto;

  &.is-recording {
    background: #e54545;
    animation: bannerPulse 1.5s ease-in-out infinite;
  }

  // Amber and deliberately still. A paused outing must not borrow the
  // live pulse — that pulse is the promise that fixes are arriving.
  &.is-paused {
    background: #ff9933;
    animation: none;
  }
}

@keyframes bannerPulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.4);
    opacity: 0.6;
  }
}

.outing-session-banner-resume {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  margin-left: 0.4rem;
  padding: 0.5rem 0.8rem;
  border: 0;
  border-radius: 999px;
  background: #ff9933;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.outing-session-banner-go {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex: 0 0 auto;
  font-size: 0.7rem;
  font-weight: 600;
  opacity: 0.8;
  white-space: nowrap;
}

.outing-session-banner-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  gap: 1px;

  strong {
    font-weight: 600;
  }
  small {
    font-size: 0.7rem;
    opacity: 0.85;
  }
}
</style>

<style lang="scss">
// Height of the banner (≈40 px) plus the 8 px it already floats above
// the nav. Applied to the single scroll surface of the mobile shell so
// the last field of a form clears the pill instead of hiding under it.
html[data-shell='mobile'][data-session-banner] .page-content {
  padding-bottom: 52px;
}

html[data-theme='dark'] {
  .outing-session-banner-btn {
    background: #ff9933;
    color: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  }
}
</style>
